/**
 * Home's layout and density, remembered across launches.
 *
 * The two controls the app bar carries (`AppBarLayoutControl`) shipped as bare `useState`, so
 * every relaunch put a resident who prefers the grid back into the list. Nothing was wrong on
 * screen and nothing logged — the preference simply had nowhere to live.
 *
 * **Why `localStorage` and not one of the two stores the app already has.** Both alternatives were
 * considered and both are wrong for this value:
 *
 *  - **Not the server.** Unlike `locale` (a cloud-owned setting with `PATCH /users/me` behind it),
 *    there is no field for this. `GateView`'s `alias`/`icon`/`color`/`sortOrder` are *per gate*
 *    and this is a property of the whole list on this handset — a phone in the grid and a tablet
 *    in the list is the correct outcome, not a conflict to resolve. Minting an account-level
 *    field for it is a PRD decision (a `D-x`), not something a screen may do on its own.
 *  - **Not the `snapshots` IndexedDB store.** That store is **derived** by contract — the gate
 *    snapshot is rebuilt from `GET /gates`, and `device-cache/profile.ts` states the rule
 *    outright: *"it lives in the derived `snapshots` store, so a schema change discards it and the
 *    next sign-in rebuilds it."* A user preference is derived from nothing and can be rebuilt by
 *    nothing, so keeping it there means an unrelated cache-version bump silently resets it —
 *    the same defect this file exists to fix, arriving later and harder to see. Its `purge` on
 *    account switch would take it too, and layout is not the departing account's property.
 *
 * `localStorage` is also the only one of the three that can be read **synchronously during the
 * first render**. IndexedDB is async, so a restored grid preference would paint the list first
 * and then jump — and a layout that flickers on every launch is barely better than one that
 * does not persist.
 *
 * The precedent is `auth/credentials.ts`'s resume hint: the one thing that deliberately lives in
 * `localStorage`, stage-scoped, carrying no secret, every access wrapped because a private-mode
 * or storage-disabled browser **throws** rather than returning null. Read that file's block
 * comment before adding a second key here — its rule is that secrets never come to this store,
 * and a layout preference is not an exception to it so much as an unrelated tenant.
 */

import { STAGE } from '../../auth/config'

export interface HomeLayoutPreference {
  readonly grid: boolean
  readonly spacious: boolean
}

export const DEFAULT_HOME_LAYOUT: HomeLayoutPreference = { grid: false, spacious: false }

/**
 * **Three modes, in the order the one app-bar pill rotates through them.** The fourth combination
 * the two booleans can express — a *large list* — is deliberately absent: the large capsule was
 * the widest tile in the app and the one whose extra height bought nothing, since the list row's
 * scarce axis is its width (`global.css` → `--puck`) and the density knob buys height only.
 *
 * The pair of booleans survives the removal because the CSS reads them as two independent classes
 * (`.home-list.grid.spacious`) and the stored row is already in that shape — a mode enum here
 * would be a stored-format change and a rewrite of every selector, for a cycle three entries long.
 * `nextHomeLayout` is what makes the pair a cycle rather than two toggles, and it is the only
 * writer of `spacious` in the app.
 */
export const HOME_LAYOUT_CYCLE: readonly HomeLayoutPreference[] = [
  { grid: false, spacious: false },
  { grid: true, spacious: false },
  { grid: true, spacious: true },
]

/**
 * The next mode in the cycle. A preference that is not *in* the cycle — the removed large list,
 * whether stored by an older build or hand-edited — rotates to the entry after the list, so a tap
 * always moves and never leaves the user where they were.
 */
export function nextHomeLayout(current: HomeLayoutPreference): HomeLayoutPreference {
  const at = HOME_LAYOUT_CYCLE.findIndex(
    (mode) => mode.grid === current.grid && mode.spacious === current.spacious,
  )
  return HOME_LAYOUT_CYCLE[(at + 1) % HOME_LAYOUT_CYCLE.length] as HomeLayoutPreference
}

const layoutKey = (stage: string): string => `selogate.${stage}.home-layout`

/**
 * The stored preference, or the default. **Never throws and never returns a partial value** — a
 * half-written or hand-edited row falls back rather than putting `undefined` into a `className`
 * expression, which renders as the string `"undefined"` in a class list and is invisible until
 * someone reads the DOM.
 */
export function readHomeLayout(stage: string = STAGE): HomeLayoutPreference {
  try {
    const raw = globalThis.localStorage?.getItem(layoutKey(stage))
    if (raw === null || raw === undefined) return DEFAULT_HOME_LAYOUT
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_HOME_LAYOUT
    const { grid, spacious } = parsed as { grid?: unknown; spacious?: unknown }
    // The large *list* is no longer a mode, and a row written by a build that still had it must
    // not resurrect it — the one pill cannot rotate back to a state it does not carry, so a
    // resident who left the app in that mode would be stuck in it until the next tap.
    return { grid: grid === true, spacious: grid === true && spacious === true }
  } catch {
    // Private mode, storage disabled, or a value that is not JSON. A preference is a convenience;
    // its absence costs a tap, never a render.
    return DEFAULT_HOME_LAYOUT
  }
}

export function writeHomeLayout(preference: HomeLayoutPreference, stage: string = STAGE): void {
  try {
    globalThis.localStorage?.setItem(layoutKey(stage), JSON.stringify(preference))
  } catch {
    /* ignore — see readHomeLayout */
  }
}
